import React from 'react';
import { useTranslation } from 'react-i18next';

import usePlatformDetect from '@/hooks/usePlatformDetect';
import { Button } from '@/ui/Button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/ui/Dialog';
import {
	Drawer,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
} from '@/ui/Drawer';

import { formatDraftUpdatedAt } from '../utils/formatDraftUpdatedAt';

type AutosaveRestoreModalProps = {
	allowEmptySource?: boolean;
	draftUpdatedAt?: string | null;
	onKeepBaseline: () => void;
	onRestoreAutosave: () => void;
	onStartEmpty?: () => void;
	open: boolean;
};

const AutosaveRestoreModal: React.FC<AutosaveRestoreModalProps> = ({
	draftUpdatedAt,
	onKeepBaseline,
	onRestoreAutosave,
	open,
}) => {
	const { t } = useTranslation();
	const platform = usePlatformDetect();
	const isDesktop = platform === 'desktop';
	const formattedDraftUpdatedAt = formatDraftUpdatedAt(draftUpdatedAt);
	const description = t('autosave.restore.descriptionWithDate', { date: formattedDraftUpdatedAt });

	const footerButtons = (
		<div className="flex flex-col gap-3 min-[480px]:flex-row min-[480px]:justify-end">
			<Button
				variant="secondary"
				className="w-full whitespace-normal text-center min-[480px]:w-auto"
				onClick={onKeepBaseline}
			>
				{t('autosave.restore.baseline')}
			</Button>

			<Button
				className="w-full whitespace-normal text-center min-[480px]:w-auto"
				onClick={onRestoreAutosave}
			>
				{t('autosave.restore.draft')}
			</Button>
		</div>
	);

	if (isDesktop) {
		return (
			<Dialog open={open} onOpenChange={() => undefined}>
				<DialogContent className="w-[calc(100vw-16px)] max-w-2xl gap-4 overflow-hidden overflow-y-auto">
					<DialogHeader className="space-y-3 pt-5 pb-4 pr-16 sm:pr-20">
						<DialogTitle className="whitespace-normal break-words">
							{t('autosave.restore.title')}
						</DialogTitle>

						<DialogDescription className="break-words">
							{description}
						</DialogDescription>
					</DialogHeader>

					<DialogFooter className="pt-4 pb-5">
						{footerButtons}
					</DialogFooter>
				</DialogContent>
			</Dialog>
		);
	}

	return (
		<Drawer open={open} onOpenChange={() => undefined}>
			<DrawerContent>
				<DrawerHeader className="gap-3 px-4 pt-4 pb-3">
					<DrawerTitle className="whitespace-normal break-words text-left">
						{t('autosave.restore.title')}
					</DrawerTitle>

					<DrawerDescription className="break-words text-left">
						{description}
					</DrawerDescription>
				</DrawerHeader>

				<DrawerFooter className="px-4 pt-3 pb-4">
					{footerButtons}
				</DrawerFooter>
			</DrawerContent>
		</Drawer>
	);
};

export { AutosaveRestoreModal };
